/**
 * Data layer for `ios-14-server-side-consented-attribution-path`. Thin typed fetch wrappers over /api/v1/ios-14-server-side-consented-attribution-path.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface Ios14ServerSideConsentedAttributionPathRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIos14ServerSideConsentedAttributionPathResponse {
	count: number;
	items: Ios14ServerSideConsentedAttributionPathRow[];
}

export interface CreateIos14ServerSideConsentedAttributionPathBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/ios-14-server-side-consented-attribution-path";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIos14ServerSideConsentedAttributionPathResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIos14ServerSideConsentedAttributionPathResponse>(res);
}

export async function create(body: CreateIos14ServerSideConsentedAttributionPathBody): Promise<Ios14ServerSideConsentedAttributionPathRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<Ios14ServerSideConsentedAttributionPathRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const ios14ServerSideConsentedAttributionPathQueryKeys = {
	all: ["ios-14-server-side-consented-attribution-path"] as const,
	list: () => [...ios14ServerSideConsentedAttributionPathQueryKeys.all] as const,
	detail: (id: string) => [...ios14ServerSideConsentedAttributionPathQueryKeys.all, id] as const,
};
